<template>
  <div class="auth-login__field">
    <div v-if="forgotLabel" class="auth-login__label-row">
      <label :for="id" class="auth-login__label">{{ label }}</label>
      <button type="button" class="auth-login__link" @click="emit('forgot')">
        {{ forgotLabel }}
      </button>
    </div>
    <label v-else :for="id" class="auth-login__label">{{ label }}</label>

    <input
      :id="id"
      :name="name || undefined"
      ref="inputRef"
      class="auth-login__input"
      :class="{ 'auth-login__input--error': error }"
      :type="type"
      :value="modelValue"
      :placeholder="placeholder"
      :autocomplete="autocomplete"
      :required="required"
      :disabled="disabled"
      :readonly="readonly"
      :inputmode="inputMode"
      :maxlength="maxLength"
      :aria-invalid="error ? 'true' : undefined"
      :aria-describedby="describedBy || undefined"
      @focus="syncFromDomValue"
      @input="onInput"
      @change="onChange"
    />

    <span v-if="hint && !error" :id="`${id}-hint`" class="auth-login__hint">{{ hint }}</span>
    <span v-if="error" :id="`${id}-error`" class="auth-login__error" role="alert">{{ error }}</span>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { formatDateOfBirth } from '../utils/formatDateOfBirth.js'
import { formatUsPhone } from '../utils/formatUsPhone.js'

const props = defineProps({
  id:           { type: String, required: true },
  name:         { type: String, default: '' },
  modelValue:   { type: String, default: '' },
  label:        { type: String, required: true },
  type:         { type: String, default: 'text' },
  placeholder:  { type: String, default: '' },
  autocomplete: { type: String, default: '' },
  inputMask:    { type: String, default: '' },
  hint:         { type: String, default: '' },
  error:        { type: String, default: '' },
  forgotLabel:  { type: String, default: '' },
  required:     { type: Boolean, default: false },
  disabled:     { type: Boolean, default: false },
  readonly:     { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'forgot'])

const inputRef = ref(null)

const describedBy = computed(() => {
  if (props.error) return `${props.id}-error`
  if (props.hint) return `${props.id}-hint`
  return ''
})

const inputMode = computed(() => {
  if (props.inputMask === 'phone') return 'tel'
  if (props.inputMask === 'phone-or-email') return 'text'
  if (props.inputMask === 'date') return 'numeric'
  if (props.inputMask === 'name') return 'text'
  return undefined
})

const maxLength = computed(() => {
  if (props.inputMask === 'phone') return 12
  if (props.inputMask === 'date') return 10
  return undefined
})

function applyMask(value) {
  if (props.inputMask === 'phone') {
    return formatUsPhone(value)
  }

  if (props.inputMask === 'date') {
    return formatDateOfBirth(value)
  }

  if (props.inputMask === 'name') {
    return value.replace(/[^\p{L}\s'-]/gu, '')
  }

  if (props.inputMask === 'phone-or-email') {
    if (/^[0-9\s\-\+\(\)]*$/.test(value)) {
      return formatUsPhone(value)
    }
    return value
  }

  return value
}

function onInput(event) {
  const masked = applyMask(event.target.value)
  event.target.value = masked
  emit('update:modelValue', masked)
}

function syncFromDomValue() {
  const domValue = inputRef.value?.value ?? ''
  const masked = applyMask(domValue)

  if (inputRef.value && inputRef.value.value !== masked) {
    inputRef.value.value = masked
  }

  if (masked !== props.modelValue) {
    emit('update:modelValue', masked)
  }
}

function onChange(event) {
  const masked = applyMask(event.target.value)
  event.target.value = masked
  emit('update:modelValue', masked)
}

onMounted(() => {
  nextTick(syncFromDomValue)
})

defineExpose({
  getInputElement: () => inputRef.value,
})
</script>
